import { create } from "zustand";
import type { RecipeFormRequest } from "@/types/recipeForm";
import type { MetaRecette } from "@/types/recipe";
import type { DeploymentSkillDocument } from "@/services/api/skills";
import type { AttachmentInfo } from "@/services/chat/attachments";
import type { RecipeExecutionUsageSummary } from "@/services/api/recipes";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  createdAt: Date;
  metadata?: {
    technical?: boolean;
    technicalRole?: string;
    streaming?: boolean;
    streamPlaceholder?: boolean;
    streamSequence?: number;
    executionId?: string;
    recipeId?: string;
    recipeName?: string;
    executionState?: string;
    resultData?: Record<string, any>;
    logs?: Array<Record<string, any> | string>;
    error?: string;
    durationMs?: number;
    usageSummary?: RecipeExecutionUsageSummary;
    resourcePreview?: {
      mimeType: string;
      previewContent: string;
      language?: string;
      truncated?: boolean;
      title?: string;
    };
    attachments?: AttachmentInfo[];
    artifacts?: AttachmentInfo[];
    /** Another person of the session wrote this message (chat channel). */
    author?: { id: string; name?: string; avatar?: string };
  };
}

export type RecipeExecutionUiStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "waiting_for_form"
  | "unknown";

export interface ExecutionRecord {
  executionId: string;
  recipeId: string;
  recipeName?: string;
  status: RecipeExecutionUiStatus;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  result?: unknown;
  error?: string;
  durationMs?: number;
  usageSummary?: RecipeExecutionUsageSummary;
  sessionId?: string;
}

export interface ChatExecutionPanelTarget {
  mode: "result" | "logs";
  executionId: string;
  recipeId?: string;
  recipeName?: string;
  resultData?: Record<string, any>;
  logs?: Array<Record<string, any> | string>;
  messageContent?: string;
}

export interface ChatFilePanelTarget {
  filename: string;
  path?: string;
  url?: string;
  size?: number;
  deploymentId?: string | null;
  fileUrl?: string;
}

export interface ExecutionStep {
  id: string;
  name: string;
  status: "pending" | "running" | "completed" | "failed";
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
  summary?: string;
  progress?: number;
}

export interface OperateStore {
  // Selected recipe
  selectedRecipeId: string | null;
  setSelectedRecipeId: (recipeId: string | null) => void;
  selectedRecipePreview: MetaRecette | null;
  setSelectedRecipePreview: (recipe: MetaRecette | null) => void;

  // Chat state
  currentSessionId: string | null;
  setCurrentSessionId: (sessionId: string | null) => void;

  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  removeMessage: (id: string) => void;
  updateLastMessage: (updates: Partial<ChatMessage>) => void;
  showTechnicalMessages: boolean;
  setShowTechnicalMessages: (show: boolean) => void;
  toggleTechnicalMessages: () => void;

  // Streaming state
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;
  streamingContent: string;
  setStreamingContent: (content: string) => void;

  // UI state
  executionSteps: ExecutionStep[];
  setExecutionSteps: (steps: ExecutionStep[]) => void;
  clearExecutionSteps: () => void;
  liveExecutionSteps: ExecutionStep[];
  setLiveExecutionSteps: (steps: ExecutionStep[]) => void;
  upsertLiveExecutionStep: (step: ExecutionStep) => void;
  clearLiveExecutionSteps: () => void;
  executionPanelOpen: boolean;
  setExecutionPanelOpen: (open: boolean) => void;
  selectedExecution: ExecutionRecord | null;
  setSelectedExecution: (execution: ExecutionRecord | null) => void;
  historyPanelOpen: boolean;
  setHistoryPanelOpen: (open: boolean) => void;
  chatExecutionPanelTarget: ChatExecutionPanelTarget | null;
  setChatExecutionPanelTarget: (
    target: ChatExecutionPanelTarget | null,
  ) => void;
  clearChatExecutionPanelTarget: () => void;
  chatFilePanelTarget: ChatFilePanelTarget | null;
  setChatFilePanelTarget: (target: ChatFilePanelTarget | null) => void;
  clearChatFilePanelTarget: () => void;
  selectedSkillIds: string[];
  setSelectedSkillIds: (skillIds: string[]) => void;
  toggleSelectedSkillId: (skillId: string) => void;
  clearSelectedSkills: () => void;
  selectedSkillDetails: Record<string, DeploymentSkillDocument>;
  hydrateSelectedSkillDetail: (skill: DeploymentSkillDocument) => void;
  activeRecipeForm: RecipeFormRequest | null;
  setActiveRecipeForm: (form: RecipeFormRequest | null) => void;
  clearActiveRecipeForm: () => void;
  recipeFormSubmitting: boolean;
  setRecipeFormSubmitting: (submitting: boolean) => void;
}

export const useOperateStore = create<OperateStore>((set) => ({
  selectedRecipeId: null,
  setSelectedRecipeId: (recipeId) => set({ selectedRecipeId: recipeId }),
  selectedRecipePreview: null,
  setSelectedRecipePreview: (selectedRecipePreview) =>
    set({ selectedRecipePreview }),

  currentSessionId: null,
  setCurrentSessionId: (sessionId) => set({ currentSessionId: sessionId }),

  messages: [],
  addMessage: (message) =>
    set((state) => ({
      messages: state.messages.some((entry) => entry.id === message.id)
        ? state.messages.map((entry) =>
            entry.id === message.id ? { ...entry, ...message } : entry,
          )
        : [...state.messages, message],
    })),
  clearMessages: () => set({ messages: [] }),
  updateMessage: (id, updates) =>
    set((state) => ({
      messages: state.messages.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              ...updates,
              metadata: updates.metadata
                ? {
                    ...(entry.metadata || {}),
                    ...updates.metadata,
                  }
                : entry.metadata,
            }
          : entry,
      ),
    })),
  removeMessage: (id) =>
    set((state) => ({
      messages: state.messages.filter((entry) => entry.id !== id),
    })),
  updateLastMessage: (updates) =>
    set((state) => {
      const messages = [...state.messages];
      if (messages.length > 0) {
        messages[messages.length - 1] = {
          ...messages[messages.length - 1],
          ...updates,
        };
      }
      return { messages };
    }),
  showTechnicalMessages: false,
  setShowTechnicalMessages: (showTechnicalMessages) =>
    set({ showTechnicalMessages }),
  toggleTechnicalMessages: () =>
    set((state) => ({
      showTechnicalMessages: !state.showTechnicalMessages,
    })),

  isStreaming: false,
  setIsStreaming: (streaming) => set({ isStreaming: streaming }),
  streamingContent: "",
  setStreamingContent: (content) => set({ streamingContent: content }),

  executionSteps: [],
  setExecutionSteps: (executionSteps) => set({ executionSteps }),
  clearExecutionSteps: () => set({ executionSteps: [] }),
  liveExecutionSteps: [],
  setLiveExecutionSteps: (liveExecutionSteps) => set({ liveExecutionSteps }),
  upsertLiveExecutionStep: (step) =>
    set((state) => {
      const existingIndex = state.liveExecutionSteps.findIndex(
        (item) => item.id === step.id,
      );
      if (existingIndex === -1) {
        return {
          liveExecutionSteps: [...state.liveExecutionSteps, step],
        };
      }

      const nextSteps = [...state.liveExecutionSteps];
      nextSteps[existingIndex] = {
        ...nextSteps[existingIndex],
        ...step,
      };

      return {
        liveExecutionSteps: nextSteps,
      };
    }),
  clearLiveExecutionSteps: () => set({ liveExecutionSteps: [] }),
  executionPanelOpen: false,
  setExecutionPanelOpen: (open) => set({ executionPanelOpen: open }),
  selectedExecution: null,
  setSelectedExecution: (selectedExecution) => set({ selectedExecution }),
  historyPanelOpen: false,
  setHistoryPanelOpen: (historyPanelOpen) => set({ historyPanelOpen }),
  chatExecutionPanelTarget: null,
  setChatExecutionPanelTarget: (chatExecutionPanelTarget) =>
    set({ chatExecutionPanelTarget }),
  clearChatExecutionPanelTarget: () => set({ chatExecutionPanelTarget: null }),
  chatFilePanelTarget: null,
  setChatFilePanelTarget: (chatFilePanelTarget) => set({ chatFilePanelTarget }),
  clearChatFilePanelTarget: () => set({ chatFilePanelTarget: null }),
  selectedSkillIds: [],
  setSelectedSkillIds: (selectedSkillIds) => set({ selectedSkillIds }),
  toggleSelectedSkillId: (skillId) =>
    set((state) => ({
      selectedSkillIds: state.selectedSkillIds.includes(skillId)
        ? state.selectedSkillIds.filter((id) => id !== skillId)
        : [...state.selectedSkillIds, skillId],
    })),
  clearSelectedSkills: () =>
    set({ selectedSkillIds: [], selectedSkillDetails: {} }),
  selectedSkillDetails: {},
  hydrateSelectedSkillDetail: (skill) =>
    set((state) => ({
      selectedSkillDetails: {
        ...state.selectedSkillDetails,
        [skill.id]: skill,
      },
    })),
  activeRecipeForm: null,
  setActiveRecipeForm: (activeRecipeForm) => set({ activeRecipeForm }),
  clearActiveRecipeForm: () => set({ activeRecipeForm: null }),
  recipeFormSubmitting: false,
  setRecipeFormSubmitting: (recipeFormSubmitting) =>
    set({ recipeFormSubmitting }),
}));
